import React, { useState, useEffect, useCallback } from "react";
import { Gift, Loader2 } from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralsTab = ({ auth, authAxios }) => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReferrals = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API_URL}/api/admin/referrals`);
      setReferrals(res.data);
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchReferrals(); }, [fetchReferrals]);

  const totalReferrals = referrals.reduce((sum, r) => sum + (r.referral_count || 0), 0);
  const activeReferrers = referrals.filter(r => !r.is_expired && r.referral_count > 0).length;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#E83E8C]">{referrals.length}</p>
          <p className="text-sm text-[#475569]">Total Codes</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#FF9F1C]">{totalReferrals}</p>
          <p className="text-sm text-[#475569]">Total Referrals</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#25D366]">{activeReferrers}</p>
          <p className="text-sm text-[#475569]">Active Referrers</p>
        </div>
      </div>

      {/* Referral Codes Table */}
      <div className="bg-white rounded-3xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-6 flex items-center gap-2">
          <Gift size={20} className="text-[#E83E8C]" /> Referral Codes
        </h2>

        {loading ? (
          <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-[#E83E8C]" /></div>
        ) : referrals.length === 0 ? (
          <p className="text-center text-[#475569] py-8">No referral codes yet. They're auto-created when customers book.</p>
        ) : (
          <div className="space-y-3">
            {referrals.map(ref => (
              <div key={ref.id} className={`rounded-2xl p-4 border ${ref.is_expired ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-[#FFFAF5] border-[#f0e6d9]'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-[#E83E8C]/10 text-[#E83E8C] rounded-full font-bold text-sm tracking-wider">{ref.code}</span>
                    <span className="text-sm font-medium text-[#1E1B4B]">{ref.customer_name}</span>
                    <span className="text-xs text-[#94a3b8]">{ref.customer_phone}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-[#FF9F1C]">{ref.referral_count} referral{ref.referral_count !== 1 ? 's' : ''}</span>
                    {ref.referral_count > 0 && <span className="px-2 py-0.5 bg-[#25D366]/10 text-[#25D366] text-xs rounded-full font-medium">{ref.referral_count}x 10% earned</span>}
                    {ref.is_expired && <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full font-medium">Expired</span>}
                  </div>
                </div>
                {ref.usages && ref.usages.length > 0 && (
                  <div className="mt-2 pl-4 border-l-2 border-[#E83E8C]/20 space-y-1">
                    {ref.usages.map((u, i) => (
                      <div key={u.new_customer_name ? `${u.new_customer_name}-${i}` : `usage-${i}`} className="flex items-center gap-2 text-xs text-[#475569]">
                        <span className="font-medium">{u.new_customer_name}</span>
                        <span>booked {u.package}</span>
                        <span className="text-[#25D366]">(5% discount given)</span>
                        <span className="text-[#94a3b8]">{u.created_at?.split('T')[0]}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-4 mt-2 text-xs text-[#94a3b8]">
                  <span>Created: {ref.created_at?.split('T')[0]}</span>
                  <span>Expires: {ref.expires_at?.split('T')[0]}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralsTab;
